import { ISO8601TimeDuration } from "@dtm-frontend/shared/utils";
import { Polygon, Properties } from "@turf/helpers";
import { Cartesian3 } from "cesium";
import { EmergencyType } from "./tactical.models";

export type RouteAreaTypeId = "flightArea" | "safetyArea" | "groundRiskArea" | "path" | "waypoint" | "blockers" | "trajectories";

export interface AssistedRiskPopulationDistribution {
    veryHighRiskPercent: number;
    highRiskPercent: number;
    mediumRiskPercent: number;
    lowRiskPercent: number;
    veryLowRiskPercent: number;
}

export interface AssistedRisk {
    avgPopulationDensity: number;
    maxPopulationDensity: number;
    intrinsicGrc: number;
    isIncompletePopulationData: boolean;
    populationDistribution: AssistedRiskPopulationDistribution;
}

export enum RpaDescriptionSectionType {
    Text = "Text",
    HeaderAbove = "HeaderAbove",
    HeaderBelow = "HeaderBelow",
    Contact = "Contact",
}

export interface RpaDescription {
    designator: string;
    sections: {
        [languageCode: string]: Array<{
            type: RpaDescriptionSectionType;
            content: string;
        }>;
    };
}

export interface MissionPlanRoute {
    routeId: string;
    planId?: string;
    missionId?: string;
    elevations?: Elevation[];
    sections: MissionPlanRouteSection[];
    isPathBased?: boolean;
    estimatedDistance?: number;
    stats?: RouteStats;
    routeProposal?: RouteProposal;
    assistedRisk?: AssistedRisk;
    rpaDescriptions?: RpaDescription[];
}

export interface RouteProposal {
    availabilityStatus: RouteAvailability;
    alternativeWindows?: {
        startAt: Date;
        endAt: Date;
    }[];
}

export enum RouteAvailability {
    Available = "AVAILABLE",
    Unavailable = "UNAVAILABLE",
    UnavailableNoAlternatives = "UNAVAILABLE_NO_ALTERNATIVES",
}

export interface RouteStats {
    adjacent: {
        bufferHorizontal: number;
        height: number;
        size: number;
    };
    flight: {
        dtmNames: string[];
        estimatedDistance: number;
        maxHeight: number;
        maxRadius: number;
        size: number;
    };
    operation: {
        maxHeight: number;
        size: number;
        maxDeclaredHeight: number;
    };
    safety: {
        height: number;
    };
}

export interface WaypointWithSection {
    waypoint: Waypoint;
    parentSection: MissionPlanRouteSection;
}

export interface MissionPlanRouteSection {
    name?: string;
    isActive?: boolean;
    flightZone?: MissionPlanRouteFlightZone;
    segment?: MissionPlanRouteSegment;
}

export interface MissionPlanRouteFlightZone {
    center: Waypoint;
    flightArea: FlightArea;
    safetyArea: Area;
    groundArea?: Polygon;
    groundAdjacentArea?: Polygon;
    stopover: {
        min: ISO8601TimeDuration;
        max: ISO8601TimeDuration;
    };
    isRunway?: boolean;
    obstacles?: ObstaclePoint;
}

export interface MissionPlanRouteSegment {
    fromWaypoint: Waypoint;
    toWaypoint: Waypoint;
    flightArea: FlightArea;
    safetyArea: Area;
    groundArea?: Polygon;
    groundAdjacentArea?: Polygon;
    distance?: number;
    elevationProfile?: {
        elevations: Elevation[];
    };
    obstacleProfile?: {
        points: SegmentObstaclePoint[];
    };
    speed?: {
        min: number;
        max: number;
    };
    duration?: {
        min: ISO8601TimeDuration;
        max: ISO8601TimeDuration;
    };
}

export interface RouteData<MissionData> {
    data?: MissionData;
    route: MissionPlanRoute;
    isPathBased?: boolean;
    sectionStatuses?: MissionSegmentStatus[];
    uniqueRouteId?: number;
    uniqueNearbyRoutesId?: number;
    nearbyMissionsData?: RouteData<MissionData>[];
    selectedMission?: MissionData;
    isSelected?: boolean;
    isCollision?: boolean;
    isMain?: boolean;
    isOutsideDtm?: boolean;
    emergency?: EmergencyType;
}

export interface Elevation {
    latitude: number;
    longitude: number;
    startOffset: number;
    value: number;
    minValue: number;
    maxValue: number;
}

export interface Waypoint {
    name: string;
    point: Point;
    estimatedArriveAt: TimeRange;
}

export interface TimeRange {
    min: Date;
    max: Date;
}

export interface Point {
    longitude: number;
    latitude: number;
    height: number;
    altitude: number;
}

export interface ObstaclePoint {
    baseline: {
        height: number;
        altitude: number;
        isActualObstacle: boolean;
    };
    area: {
        height: number;
        altitude: number;
        isActualObstacle: boolean;
    };
}

export interface SegmentObstaclePoint extends Partial<ObstaclePoint> {
    longitude: number;
    latitude: number;
    startOffsetMeters: number;
}

export interface Area {
    elevationMax?: number;
    elevationMin?: number;
    volume: {
        area: Polygon;
        ceiling: number;
        floor: number;
        coreCeiling?: number;
        coreFloor?: number;
        size?: number;
    };
}

export interface FlightArea extends Area {
    safetyBuffer?: {
        horizontal: number;
        vertical: number;
    };
    properties: Properties;
}

export enum MissionSegmentStatus {
    ACTIVE_SEGMENT = "ACTIVE_SEGMENT",
    DEFAULT = "DEFAULT",
    MISSION_ACTIVATED = "MISSION_ACTIVATED",
    MISSION_NOT_ACTIVATED = "MISSION_NOT_ACTIVATED",
    NOT_ACTIVE_SEGMENT = "NOT_ACTIVE_SEGMENT",
    DEACTIVATED_SEGMENT = "DEACTIVATED_SEGMENT",
}

export interface PinAcEntity<T> {
    id: string | number | undefined;
    show?: boolean;
    data?: PinData<T>;
    position: Cartesian3;
}

export interface PinData<T> {
    flightArea: FlightArea;
    waypoints: Waypoint[];
    isSelected?: boolean;
    isMain?: boolean;
    isCollision?: boolean;
    data?: T;
    status?: MissionSegmentStatus;
    index?: number;
}

export interface AircraftEntity {
    uri?: string;
    name?: string;
    aircraftType: AircraftType;
    updateTime?: number;
    isConnectionLost?: boolean;
    isFaradaSource?: boolean;
    trackerIdentifier?: string;
    operatorId?: string | null;
    operator?: FlightOperator;
    position: FlightPosition & { positionCartesian: Cartesian3 };
    sourceType: FlightSourceType;
}

export interface FlightPositionUpdate {
    updateType: FlightPositionUpdateType;
    aircraftType: AircraftType;
    id: string;
    trackerIdentifier: string;
    callSign?: string;
    name?: string;
    isConnectionLost?: boolean;
    isFaradaSource?: boolean;
    orientation?: number;
    operatorId?: string | null;
    receivedSignalStrengthIndication?: number;
    operator?: FlightOperator;
    position: FlightPosition;
    sourceType: FlightSourceType;
}

export enum FlightPositionUpdateType {
    Start = "Start",
    Update = "Update",
    End = "End",
}

export enum AircraftType {
    Uav = "Uav",
    Airplane = "Airplane",
    Receiver = "Receiver",
    Other = "Other",
}

export interface Trajectory {
    id?: string;
    flightId?: string;
    trackerIdentifier?: string;
    startTime?: Date;
    endTime?: Date;
    positions: TrajectoryPosition[];
    isActive?: boolean;
}

export interface TrajectoryPosition extends Omit<TrajectoryPositionBodyResponse, "timestamp"> {
    timestamp?: Date;
}

export interface TrajectoryPositionBodyResponse {
    timestamp?: string;
    latitude: number;
    longitude: number;
    altitude?: number;
    height?: number;
}

export interface HemsDataResponseBody {
    content: HemsContent[];
    pageable: {
        page: number;
        size: number;
        sort: {
            orders: [];
            empty: boolean;
            unsorted: boolean;
            sorted: boolean;
        };
        offset: number;
        pageNumber: number;
        pageSize: number;
        paged: boolean;
        unpaged: boolean;
    };
    total: number;
    totalPages: number;
    totalElements: number;
    last: boolean;
    size: number;
    number: number;
    sort: {
        orders: [];
        empty: boolean;
        unsorted: boolean;
        sorted: boolean;
    };
    numberOfElements: number;
    first: boolean;
    empty: boolean;
}

interface HemsContent {
    name: HemsEvents;
    body: string;
}

export interface HemsBasicEventData {
    name: HemsEvents;
    content: HemsData;
}

export interface HemsData {
    identifier: string;
    lastPosition: {
        longitude: number;
        latitude: number;
        altitude: number;
    };
    lastPositionChangeTime?: Date | null;
    associatedTeam: string;
    landing?: {
        position: {
            longitude: number;
            latitude: number;
            altitude: number | null;
        };
        areaRadius: number;
        distanceToCenter: number;
        activatedAt: Date | null;
        estimatedArrivalTime: Date | null;
        id: string;
    };
    departure?: {
        position: {
            longitude: number;
            latitude: number;
            altitude: number | null;
        };
        areaRadius: number;
        distanceToCenter: number;
        activatedAt: Date | null;
        estimatedArrivalTime: Date | null;
        id: string;
    };
    createdAt: Date;
    predictedPosition?: {
        arrivalTime?: Date;
        position: {
            longitude: number;
            latitude: number;
            altitude: number | null;
        };
    };
}

export interface HemsAreaData extends HemsData {
    areaType: string;
    areaId: string;
}

export interface HemsAreaActivationEventData extends HemsBasicEventData {
    name: HemsEvents.AreaActivatedEvent | HemsEvents.AreaDeactivatedEvent;
    content: HemsAreaData;
}

export enum HemsEvents {
    OperationStartedEvent = "OperationStartedEvent",
    AreaActivatedEvent = "AreaActivatedEvent",
    AreaDeactivatedEvent = "AreaDeactivatedEvent",
    AircraftStatusChangedEvent = "AircraftStatusChangedEvent",
    AircraftPositionChangedEvent = "AircraftPositionChangedEvent",
}

export type HemsEventData = HemsBasicEventData | HemsAreaActivationEventData;

export interface SectionElementResponseBody {
    active: boolean;
    flightZone: {
        center: Waypoint;
        flightArea: FlightArea;
        safetyArea: Area;
        ground?: {
            area: Polygon;
        };
        groundAdjacent?: {
            area: Polygon;
        };
        stopover: {
            min: ISO8601TimeDuration;
            max: ISO8601TimeDuration;
        };
        runway: boolean;
        obstacles?: {
            baseline: {
                height: number;
                altitude: number;
                obstacleExists: boolean;
            };
            area: {
                height: number;
                altitude: number;
                obstacleExists: boolean;
            };
        };
    };
    segment: {
        from: Waypoint;
        to: Waypoint;
        flightArea: FlightArea;
        safetyArea: Area;
        ground?: {
            area: Polygon;
        };
        groundAdjacent?: {
            area: Polygon;
        };
        elevationProfile: {
            elevations: Elevation[];
        };
        obstacleProfile?: {
            points: Array<{
                longitude: number;
                latitude: number;
                startOffsetMeters: number;
                baseline: {
                    height: number;
                    altitude: number;
                    obstacleExists: boolean;
                };
                area: {
                    height: number;
                    altitude: number;
                    obstacleExists: boolean;
                };
            }>;
        };
        distance?: number;
        speed?: {
            min: number;
            max: number;
        };
        duration?: {
            min: ISO8601TimeDuration;
            max: ISO8601TimeDuration;
        };
    };
}

export interface FlightPositionUpdateResponseBody {
    flightId: string;
    trackerIdentifier: string;
    sourceType: FlightSourceType;
    operatorId?: string | null;
    uav?: {
        name: string;
    };
    position: FlightPosition;
    lastSource: string;
    sources: string[];
    createdAt: string;
    operator?: FlightOperator;
    signal?: {
        rssi: number;
        connectionActive: boolean;
    };
}

export enum FlightMissionPhase {
    Active = "Active",
    Started = "Started",
    Incoming = "Incoming",
    Finished = "Finished",
}

export enum FlightSourceType {
    Adsb = "ADSB,",
    Gnss = "GNSS,",
    Flarm = "FLARM,",
    RemoteId = "REMOTE_ID,",
    Other = "OTHER",
}

export interface FlightOperator {
    remoteIdOperatorId: string;
    latitude: number;
    longitude: number;
}

export interface FlightPosition {
    track?: number;
    latitude: number;
    longitude: number;
    altitude: number | null;
    height: number | null;
    originalAltitudeBaro: number;
    originalAltitudeGeo: number;
    originalHeight: number;
    originalMslAltitude: number;
    heightGeo: number | null;
    source: string;
    groundSpeed: number | null;
    elevation: number | null;
    accuracy: {
        latitude: number;
        longitude: number;
        altitude: number;
    };
}
